import { useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check } from "lucide-react";

// Nettoyage doux du "bruit" typique des sorties LLM avant rendu.
function cleanAi(raw) {
  if (!raw || typeof raw !== "string") return "";
  let t = raw.trim();
  // Déballe un bloc de code qui enrobe TOUTE la réponse (```md ... ```)
  const fence = t.match(/^```[a-zA-Z]*\n([\s\S]*?)\n```$/);
  if (fence) t = fence[1].trim();
  // Normalise les tirets longs isolés en séparateurs markdown propres
  t = t.replace(/^\s*[—–]{2,}\s*$/gm, "---");
  // Retire les accolades JSON résiduelles esseulées en début/fin
  t = t.replace(/^\s*[{}]\s*$/gm, "");
  // Compacte les lignes vides multiples
  t = t.replace(/\n{3,}/g, "\n\n");
  return t.trim();
}

const components = {
  h1: ({ node, ...p }) => <h2 className="text-base font-semibold text-zinc-100 mt-4 mb-2 first:mt-0" {...p} />,
  h2: ({ node, ...p }) => <h3 className="text-sm font-semibold text-zinc-100 mt-4 mb-1.5 first:mt-0 pb-1 border-b border-white/10" {...p} />,
  h3: ({ node, ...p }) => <h4 className="text-sm font-semibold text-zinc-200 mt-3 mb-1 first:mt-0" {...p} />,
  h4: ({ node, ...p }) => <h5 className="text-[13px] font-semibold text-zinc-200 mt-3 mb-1 first:mt-0" {...p} />,
  p: ({ node, ...p }) => <p className="text-sm text-zinc-300 leading-relaxed my-2 first:mt-0 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-2 pl-5 list-disc marker:text-zinc-600 space-y-1" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-2 pl-5 list-decimal marker:text-zinc-600 space-y-1" {...p} />,
  li: ({ node, ...p }) => <li className="text-sm text-zinc-300 leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-zinc-100" {...p} />,
  // Italiques atténués (demande explicite : moins de clutter visuel)
  em: ({ node, ...p }) => <span className="not-italic text-zinc-200" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#EF9F27] hover:underline" target="_blank" rel="noreferrer" {...p} />,
  code: ({ node, inline, ...p }) =>
    inline
      ? <code className="px-1 py-0.5 rounded bg-white/10 text-[12px] font-mono text-zinc-200" {...p} />
      : <code className="block p-3 rounded-lg bg-black/40 border border-white/10 text-[12px] font-mono text-zinc-200 overflow-x-auto" {...p} />,
  hr: () => <hr className="my-4 border-white/10" />,
  blockquote: ({ node, ...p }) => <blockquote className="my-2 border-l-2 border-[#EF9F27]/40 pl-3 text-zinc-400 text-sm" {...p} />,
  table: ({ node, ...p }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-white/10">
      <table className="w-max min-w-full text-[13px] border-collapse" {...p} />
    </div>
  ),
  thead: ({ node, ...p }) => <thead className="bg-white/5" {...p} />,
  th: ({ node, ...p }) => <th className="text-left font-semibold text-zinc-200 px-3 py-2 border-b border-white/10 whitespace-nowrap" {...p} />,
  td: ({ node, ...p }) => <td className="text-zinc-300 px-3 py-2 border-b border-white/5 align-top min-w-[72px] max-w-[340px] [overflow-wrap:normal] [word-break:normal]" {...p} />,
};

export function AiText({ children, className = "", testId, copyable = true }) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => cleanAi(children), [children]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* noop */ }
  };

  if (!text) return null;

  return (
    <div className={`relative group ${className}`} data-testid={testId}>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          className="absolute -top-1 right-0 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold text-zinc-400 bg-white/5 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity hover:text-zinc-100"
          data-testid={testId ? `${testId}-copy` : "aitext-copy"}
          title="Copier le texte"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copié" : "Copier"}
        </button>
      )}
      <div className="max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {text}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default AiText;
